import { hasPaidSubscription } from "refsix-core";
import { store } from "../redux/store";
import { getStoreReceipt } from "./deviceReceiptService";
import { isAndroid, isIos } from "./platformDetection";
import request from "./request";
import { refreshSubscriptions } from "./subscriptionService";

// REFSIX-764: the "we found two REFSIX accounts" modal needs more than the
// other account's username — it renders the account's email, match count,
// last-match date and plan tier. These come from the authenticated
// /account/receipt-account-info endpoint (the modal only ever shows for a
// logged-in user), NOT the unauthenticated /lookup-username-by-receipt
// prefill endpoint, which deliberately returns only a username.
export interface SubscriptionAccountMismatch {
    currentUsername: string;
    receiptUsername: string;
    // The other account's email (may be null if the customer doc has none).
    email: string | null;
    // Number of matches on the other account, and the ISO date of the most
    // recent one (null when the account has no matches). Drive the design's
    // "which has 12 matches, the last match being 8 months ago" copy.
    matchCount: number;
    lastMatchDate: string | null;
    // Plan tier on the other account: "pro" → "PRO", "basic" → "Plus".
    // null when the account has no active paid plan (shouldn't happen for a
    // receipt-owning account, but the modal degrades gracefully).
    planFeatures: "pro" | "basic" | null;
}

// Shape of POST /account/receipt-account-info. A discriminated result:
// `mismatch: false` means the receipt resolves only to the caller (or to
// nobody), so no prompt is needed.
interface ReceiptAccountInfoResponse {
    mismatch: boolean;
    receiptUsername?: string;
    email?: string | null;
    matchCount?: number;
    lastMatchDate?: string | null;
    planFeatures?: "pro" | "basic" | null;
    subscriptionStatus?: string;
}

export interface SubscriptionTransferMatchesSummary {
    copiedCount: number;
    conflicts: number;
    gpsCopied: number;
    gpsFailed: number;
    hrCopied: number;
    hrFailed: number;
    reason?: string;
}

export interface SubscriptionTransferResult {
    transferredSubscriptions: number;
    transferredMatches: boolean;
    // True when this call cleaned up a prior partial-failure (target
    // already had every sub, but source still had them — i.e. an
    // earlier attempt's source-write crashed and this attempt finished
    // the job). The server's transferredSubscriptions counts the
    // *delta* on this call, which is 0 in that case; this flag is the
    // signal that something meaningful actually happened so the UI can
    // show a success toast rather than silently dismissing.
    convergedFromRetry: boolean;
    sourceUsername: string | null;
    matchesSummary?: SubscriptionTransferMatchesSummary;
}

// POSTs the device receipt to the authenticated enrichment endpoint and
// validates the response shape at runtime (the TS generic on request.post
// is compile-time only — a stale/buggy server could return anything).
// Returns null on any failure or malformed body so the detector treats
// every failure uniformly as "no prompt", with a console.warn breadcrumb.
const fetchReceiptAccountInfo = async (receiptPayload: {
    platform: string;
    receipt: unknown;
}): Promise<ReceiptAccountInfoResponse | null> => {
    try {
        const response = await request.post<ReceiptAccountInfoResponse>(
            "/account/receipt-account-info",
            {
                platform: receiptPayload.platform,
                receipt: receiptPayload.receipt,
            }
        );
        const data = response && response.data;
        if (!data || typeof data.mismatch !== "boolean") {
            return null;
        }
        return data;
    } catch (err) {
        console.warn("fetchReceiptAccountInfo failed", err);
        return null;
    }
};

// Detects the REFSIX-41 case: the on-device App Store / Play Store receipt
// validates to a different REFSIX account than the one the user is signed
// into right now. REFSIX-683 server-side transfer deliberately skips this
// case (the sub is still active on the other account, so it's unsafe to
// silently yank), which leaves the paying user with no PRO entitlement
// and no diagnostic — the chronic Zendesk issue this ticket exists to fix.
//
// Re-uses the REFSIX-732 primitives: getStoreReceipt() for deterministic
// active-record selection on device, lookupUsernameByReceipt() for the
// server-side validate+lookup (returns null on miss / 4xx / 5xx, with the
// 500 path already split for SRE alerting).
//
// Returns null on every "no prompt needed" outcome (not logged in, already
// paid on this account, no receipt on device, no matching REFSIX account
// for the receipt, receipt owner matches current login, transient failure)
// so the caller can treat null uniformly. Silent on every failure with a
// console.warn breadcrumb, matching the rest of the receipt pipeline.
export const detectSubscriptionAccountMismatch = async (): Promise<
    SubscriptionAccountMismatch | null
> => {
    try {
        if (!isIos() && !isAndroid()) {
            return null;
        }

        const state = store.getState();
        const currentUsername = state.auth?.session?.user_id;
        if (!currentUsername) {
            return null;
        }

        // If the signed-in account already has its own paid sub, the bug
        // this ticket targets can't apply — don't prompt regardless of
        // what receipt sits on device.
        const subscriptions = state.subscriptions?.subscriptions || [];
        if (hasPaidSubscription(subscriptions)) {
            return null;
        }

        const receiptPayload = await getStoreReceipt();
        if (!receiptPayload) {
            return null;
        }

        // Single authenticated round-trip: the server validates the receipt,
        // decides whether it belongs to a DIFFERENT account than the caller,
        // and — if so — returns that account's email / match stats / plan so
        // the modal can render the full design copy. `mismatch: false` covers
        // every "no prompt" case (resolves to the caller, or to nobody).
        const info = await fetchReceiptAccountInfo(receiptPayload);
        if (!info || !info.mismatch || !info.receiptUsername) {
            return null;
        }
        const receiptUsername = info.receiptUsername;

        // Re-check post-await: the user may have logged out / been logged
        // out (session expiry) while the network call was in flight.
        const stateNow = store.getState();
        const currentUsernameNow = stateNow.auth?.session?.user_id;
        if (!currentUsernameNow) {
            return null;
        }

        // Defensive case-insensitive re-check. The server already excluded
        // the caller, but the session's user_id is whatever the user typed
        // at login (CouchDB usernames are case-sensitive but emails are not);
        // never prompt someone to "switch" to their own account on a case
        // difference alone.
        if (
            currentUsernameNow.toLowerCase() === receiptUsername.toLowerCase()
        ) {
            return null;
        }

        return {
            currentUsername: currentUsernameNow,
            receiptUsername,
            email: info.email ?? null,
            matchCount:
                typeof info.matchCount === "number" ? info.matchCount : 0,
            lastMatchDate: info.lastMatchDate ?? null,
            planFeatures: info.planFeatures ?? null,
        };
    } catch (err) {
        // Silent to the user — worst-case outcome is they keep seeing the
        // "no PRO" experience they already have. But emit a warn so a
        // future regression in this orchestration leaves a Sentry /
        // console breadcrumb instead of disabling the prompt with no
        // diagnostic signal.
        console.warn("detectSubscriptionAccountMismatch failed", err);
        return null;
    }
};

// REFSIX-41 "Move Sub" action. Re-fetches the device receipt (cheap; it's
// on-disk and the cdvPurchase init is already memoised by the time the
// alert renders), hands it to the authenticated transfer endpoint, and on
// success refreshes the local subscription state so the user's PRO
// entitlement appears without requiring a logout.
//
// `includeMatches` toggles the matches-copy path on the server: the
// source user's match docs are bulk-copied into the target's match db,
// and the per-user S3 prefix under gps-store + heart-rate-store is
// copy-synced. Both are best-effort — a matches-step failure surfaces
// in the result (transferredMatches:false, matchesSummary.reason) but
// never undoes the sub move that already succeeded.
//
// Returns the server's result on success, or null on any failure
// (no receipt, network error, server-side rejection). Caller treats null
// as "show error toast, leave alert open so the user can retry or pick a
// different option" — same silent-failure-with-warn pattern as the
// detection step.
export const transferSubscriptionFromReceipt = async (
    includeMatches: boolean = false
): Promise<SubscriptionTransferResult | null> => {
    try {
        const receiptPayload = await getStoreReceipt();
        if (!receiptPayload) {
            // No receipt = nothing to transfer. Shouldn't reach here in
            // practice because the alert only opens after detection found
            // a receipt; treat as a silent no-op so a race can't crash.
            return null;
        }

        const response = await request.post<SubscriptionTransferResult>(
            "/account/transfer-from-receipt",
            {
                platform: receiptPayload.platform,
                receipt: receiptPayload.receipt,
                includeMatches,
            }
        );

        const data = response && response.data;
        // The TS generic on request.post is compile-time only — validate
        // the fields the UI branches on at runtime. A stale or buggy server
        // returning e.g. `transferredMatches: "true"` (string) would be
        // truthy and route the alert into the "matches moved" toast even
        // though no matches were actually copied. Reject the whole result
        // on a shape mismatch (null = silent failure → error toast,
        // alert stays open for retry) rather than render something we
        // can't trust.
        if (
            !data ||
            typeof data.transferredSubscriptions !== "number" ||
            typeof data.transferredMatches !== "boolean"
        ) {
            return null;
        }

        // Normalise convergedFromRetry — older servers without this
        // field will return undefined, which we coerce to false so
        // callers can rely on a strict boolean.
        const convergedFromRetry =
            typeof data.convergedFromRetry === "boolean"
                ? data.convergedFromRetry
                : false;
        const normalised: SubscriptionTransferResult = {
            ...data,
            convergedFromRetry,
        };

        // Pull fresh sub state so the UI shows PRO immediately instead of
        // waiting for the next periodic refresh. Best-effort — if the
        // refresh itself fails, the move still happened server-side and
        // a later refresh will catch up. Don't fail the whole call.
        //
        // Trigger on either: (a) new subs moved this call, or (b) a
        // retry that converged a prior partial-failure (subs are now
        // exclusively on target after we cleared the leftover source
        // copies). Both states mean the user's entitlement has just
        // changed in a way the local cache needs to learn about.
        if (
            normalised.transferredSubscriptions > 0 ||
            normalised.convergedFromRetry
        ) {
            try {
                await refreshSubscriptions();
            } catch (refreshErr) {
                console.warn(
                    "transferSubscriptionFromReceipt: refreshSubscriptions failed",
                    refreshErr
                );
            }
        }

        return normalised;
    } catch (err) {
        console.warn("transferSubscriptionFromReceipt failed", err);
        return null;
    }
};
